import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePreview } from '@dashboard/context/PreviewContext';

// Price-picker options are card-like items inheriting their surface from the
// Cards element (like variants/tickets), so the breadcrumb leads with Cards,
// matching the cp_variant "Cards / Product variants" convention.
const PRICE_OPTIONS_BLOCK = {
  section: 'cards',
  label: 'Cards',
  subsection: 'price-options',
  sublabel: 'Price options',
  relationship: 'hierarchy',
};

/**
 * Block-level cp_ classes that get hover outlines and click-to-scroll.
 * Only these outermost wrappers are interactive — inner elements (like
 * individual price breakdown items) are not highlighted separately.
 *
 * Map of exact class name → { section, label, subsection?, sublabel?,
 * subsection2?, sublabel2? }. subsection2/sublabel2 add a third breadcrumb
 * segment for entries nested two levels deep (e.g. Cards › Product variants
 * › Product variants PWYW); only 'hierarchy' relationships support a third
 * segment.
 */
const BLOCK_CLASS_TO_SECTION = {
  // Logo
  'cp_sold-by-wrapper': { section: 'logo', label: 'Logo' },
  'cp_product-logo': { section: 'logo', label: 'Logo' },

  // Product
  'cp_product-title-container': { section: 'product', label: 'Product' },

  // Pricing — the single-price block ("Price") and the multi-price picker
  // ("Price options") are now separate design sections.
  'cp_product-price-wrapper': { section: 'prices', label: 'Price' },
  'cp_price-picker': PRICE_OPTIONS_BLOCK,
  // Also holds the heading and description above the options, so the
  // outermost-ancestor rule outlines them with the options as one element.
  'cp_price-picker-block': PRICE_OPTIONS_BLOCK,

  // Pay what you want — all three inputs (single-price, multi-price option,
  // variant) are styled by BOTH the Fields chain (their base) and the PWYW
  // elements, so they show independent "Fields · PWYW" badges (relationship:
  // 'multi', like the Stripe payment element) — each badge jumps to its own
  // section.
  //
  // The multi-price option's PWYW label (option header) and amount input
  // (option body) have no shared wrapper, so each entry unions the other into
  // its highlight box (unionWithin scopes the lookup to this option's card).
  'cp_pwyw-wrapper': {
    section: 'fields',
    label: 'Fields',
    subsection: 'pwyw',
    sublabel: 'PWYW',
    relationship: 'multi',
  },
  'cp_price-picker-option-pwyw': {
    section: 'fields',
    label: 'Fields',
    subsection: 'pwyw',
    sublabel: 'PWYW',
    relationship: 'multi',
    unionWithin: 'cp_price-picker-option',
    unionClasses: ['cp_price-picker-option-pwyw-label'],
  },
  'cp_price-picker-option-pwyw-label': {
    section: 'fields',
    label: 'Fields',
    subsection: 'pwyw',
    sublabel: 'PWYW',
    relationship: 'multi',
    unionWithin: 'cp_price-picker-option',
    unionClasses: ['cp_price-picker-option-pwyw'],
  },

  // Description
  'cp_product-description-wrapper': { section: 'description', label: 'Description' },

  // Event
  'cp_event-details-wrapper': { section: 'product', label: 'Product' },

  // Cards (Variants / Tickets / Manual payments)
  // relationship: 'hierarchy' → "Cards › Product variants" (parent-child, inherits from Cards)
  cp_variant: {
    section: 'cards',
    label: 'Cards',
    subsection: 'variants',
    sublabel: 'Product variants',
    relationship: 'hierarchy',
  },
  // Nested one level deeper than cp_variant (which it sits inside): the
  // more specific class wins since findBlockFromTarget matches the nearest
  // ancestor first, so hovering the input itself yields this entry rather
  // than cp_variant's 2-segment one. Styled by both the Fields chain and the
  // PWYW elements, so it shows the "Fields · Pay what you want" badges like
  // the multi-price PWYW input.
  'cp_variant-pwyw-input': {
    section: 'fields',
    label: 'Fields',
    subsection: 'pwyw',
    sublabel: 'PWYW',
    relationship: 'multi',
  },
  'cp_ticket-group': {
    section: 'cards',
    label: 'Cards',
    subsection: 'ticket-types',
    sublabel: 'Ticket types',
    relationship: 'hierarchy',
  },
  // Same story as cp_variant-pwyw-input: nested inside cp_ticket-group, and the
  // nearest-ancestor match means the amount box points at the PWYW section
  // rather than the enclosing Ticket types card.
  'cp_ticket-type-pwyw-input': {
    section: 'fields',
    label: 'Fields',
    subsection: 'pwyw',
    sublabel: 'PWYW',
    relationship: 'multi',
  },
  'cp_payment-options-wrapper': {
    section: 'cards',
    label: 'Cards',
    subsection: 'manual-payments',
    sublabel: 'Manual payments',
    relationship: 'hierarchy',
  },
  'cp_order-bumps': {
    section: 'cards',
    label: 'Cards',
    subsection: 'order-bumps',
    sublabel: 'Order bumps',
    relationship: 'hierarchy',
  },

  // Form fields (the block, not individual items)
  'cp_form-fields': { section: 'fields', label: 'Fields' },
  'cp_guests': { section: 'fields', label: 'Fields' },

  // Discount code
  'cp_coupon-wrapper': { section: 'fields', label: 'Fields' },

  // Price breakdown (the whole block)
  'cp_price-breakdown-wrapper': { section: 'price-breakdown', label: 'Price breakdown' },
  'cp_cover-processing-fees': {
    section: 'cards',
    label: 'Cards',
    subsection: 'cover-processing-fees',
    sublabel: 'Processing fee',
    relationship: 'hierarchy',
  },

  // Payment elements
  'cp_stripe-card-element-wrapper': { section: 'fields', label: 'Fields' },
  // relationship: 'multi' → "Fields · Cards" (styled by both independent sections)
  'cp_stripe-payment-element': {
    section: 'fields',
    label: 'Fields',
    subsection: 'cards',
    sublabel: 'Cards',
    relationship: 'multi',
  },

  // Button
  'cp_pay-button-wrapper': { section: 'button', label: 'Button' },
  cp_button: { section: 'button', label: 'Button' },

  // Trust badges
  'cp_express-checkout-wrapper': { section: 'trust-badges', label: 'Trust badges' },
  'cp_pay-with-card-wrapper': { section: 'trust-badges', label: 'Trust badges' },
  'cp_secure-payments': { section: 'trust-badges', label: 'Trust badges' },
  'cp_powered-by-stripe-logo': { section: 'trust-badges', label: 'Trust badges' },
  'cp_powered-by-checkoutpage-pill': { section: 'trust-badges', label: 'Trust badges' },

  // Confirmation (inner wrapper, excludes the button)
  'cp_status-content-wrapper': { section: 'confirmation', label: 'Confirmation' },

  // Divider
  cp_divider: { section: 'columns', label: 'Columns' },
};

/**
 * Check if a DOM element has a block-level cp_ class.
 * Returns the section ID string or null.
 */
function getBlockFromElement(element) {
  const classes = element.classList;
  for (let i = 0; i < classes.length; i++) {
    const entry = BLOCK_CLASS_TO_SECTION[classes[i]];
    if (entry) return entry;
  }
  return null;
}

const FORM_INPUT_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

// Blocks where nested form inputs get their own highlight → 'fields'
const INPUT_OVERRIDE_BLOCKS = new Set(['cp_variants-wrapper', 'cp_ticket-types-wrapper']);

/**
 * Check if the target is a form input nested inside a variant/ticket block.
 * These inputs get their own highlight and navigate to 'fields'.
 */
function isInputInOverrideBlock(target, container) {
  let hasInput = false;
  let el = target;
  while (el && el !== container) {
    if (FORM_INPUT_TAGS.has(el.tagName)) hasInput = true;
    if (hasInput) {
      for (const cls of el.classList) {
        if (INPUT_OVERRIDE_BLOCKS.has(cls)) return true;
      }
    }
    // Stop at any block boundary
    if (hasInput && getBlockFromElement(el)?.section) return false;
    el = el.parentElement;
  }
  return false;
}

/**
 * Walk up from a DOM element to find the nearest block-level cp_ ancestor.
 * Returns { section, element } or null.
 *
 * Exception: form inputs inside variant/ticket blocks get their own
 * highlight and navigate to 'fields' instead of the parent block.
 */
export function findBlockFromTarget(target, container) {
  if (isInputInOverrideBlock(target, container)) {
    let el = target;
    while (el && el !== container) {
      if (FORM_INPUT_TAGS.has(el.tagName))
        return {
          section: 'fields',
          label: 'Fields',
          element: el,
          subsection: null,
          sublabel: null,
          subsection2: null,
          sublabel2: null,
        };
      el = el.parentElement;
    }
  }

  let el = target;
  while (el && el !== container) {
    const block = getBlockFromElement(el);
    if (block) {
      // Prefer the OUTERMOST ancestor mapping to the same section: the price
      // picker nests its options list inside the grouped-tabs wrapper (both
      // carry cp_price-picker), and the outline must wrap the tabs bar and
      // the options list together, not just the hovered half.
      let element = el;
      let walker = el.parentElement;
      while (walker && walker !== container) {
        const outer = getBlockFromElement(walker);
        if (outer && outer.section === block.section) element = walker;
        walker = walker.parentElement;
      }
      return {
        section: block.section,
        label: block.label,
        element,
        subsection: block.subsection || null,
        sublabel: block.sublabel || null,
        subsection2: block.subsection2 || null,
        sublabel2: block.sublabel2 || null,
        relationship: block.relationship || null,
        unionWithin: block.unionWithin || null,
        unionClasses: block.unionClasses || null,
      };
    }
    el = el.parentElement;
  }
  return null;
}

const scrollToSection = sectionId => {
  // Expand the section first so it's measurable before scrolling
  window.dispatchEvent(new CustomEvent('expand-design-section', { detail: sectionId }));

  // Wait for the collapsible open animation (150ms) to finish before measuring position,
  // otherwise the scroll target shifts mid-scroll as the section expands
  setTimeout(() => {
    const element = document.getElementById(sectionId);
    if (element) {
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const elementTop = rect.top + scrollTop;
      const offsetPosition = elementTop - 64;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, 160);
};

// The AI editor outlines its changes in the same blue (PreviewHighlights).
export const HIGHLIGHT_COLOR = '#4c96f9';
const LABEL_BG = '#e8e8e8';
const LABEL_BG_HOVER = '#d4d4d4';
const LABEL_BG_PARENT = '#cfcfcf';
const LABEL_BG_PARENT_HOVER = '#c0c0c0';
const LABEL_COLOR = '#404040';
const ARROW_SIZE = 2; // px – width of the CSS chevron arrow

/**
 * Create an absolutely-positioned overlay div that shows the highlight border
 * on top of the target element without touching its inline styles.
 */
function createOverlay({
  section,
  label,
  subsection,
  sublabel,
  subsection2,
  sublabel2,
  relationship,
}) {
  const el = document.createElement('div');
  el.style.cssText = `
    position: absolute;
    pointer-events: none;
    border: 1px solid ${HIGHLIGHT_COLOR};
    z-index: 9999;
    transition: none;
  `;

  const labelContainer = document.createElement('div');
  labelContainer.style.cssText = `
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: stretch;
    white-space: nowrap;
    height: 16px;
  `;

  const labelPartStyle = `
    padding: 1px 3px;
    color: ${LABEL_COLOR};
    font-size: 10px;
    line-height: 14px;
    font-weight: 500;
    white-space: nowrap;
    cursor: default;
  `;

  if (sublabel) {
    const isHierarchy = relationship === 'hierarchy';

    if (isHierarchy) {
      // Chevron-chained breadcrumb: 2 segments (Cards › Product variants) or
      // 3 (Cards › Product variants › Product variants PWYW). Each segment
      // is its own clickable badge; only the first uses the darker "parent"
      // background, and only the middle segment (when there are 3) needs a
      // clip-path with both an incoming notch AND an outgoing arrow.
      const segments = [
        { target: section, text: label },
        { target: subsection, text: sublabel },
      ];
      if (sublabel2) segments.push({ target: subsection2, text: sublabel2 });

      segments.forEach((segment, index) => {
        const isFirst = index === 0;
        const isLast = index === segments.length - 1;
        const bg = isFirst ? LABEL_BG_PARENT : LABEL_BG;
        const bgHover = isFirst ? LABEL_BG_PARENT_HOVER : LABEL_BG_HOVER;
        const clipPath = isFirst
          ? `polygon(0 0, calc(100% - ${ARROW_SIZE}px) 0, 100% 50%, calc(100% - ${ARROW_SIZE}px) 100%, 0 100%)`
          : isLast
            ? `polygon(0 0, ${ARROW_SIZE}px 50%, 0 100%, 100% 100%, 100% 0)`
            : `polygon(0 0, calc(100% - ${ARROW_SIZE}px) 0, 100% 50%, calc(100% - ${ARROW_SIZE}px) 100%, 0 100%, ${ARROW_SIZE}px 50%)`;

        const wrap = document.createElement('div');
        wrap.style.cssText = `
          display: flex;
          align-items: center;
          pointer-events: auto;
          cursor: pointer;
          background-color: ${bg};
          padding-left: ${isFirst ? 0 : ARROW_SIZE}px;
          padding-right: ${isLast ? 0 : ARROW_SIZE}px;
          clip-path: ${clipPath};
        `;
        if (index === 0) wrap.dataset.section = segment.target;
        else if (index === 1) wrap.dataset.subsection = segment.target;
        else wrap.dataset.subsection2 = segment.target;

        const segmentLabelEl = document.createElement('span');
        segmentLabelEl.textContent = segment.text;
        segmentLabelEl.style.cssText = labelPartStyle;
        wrap.appendChild(segmentLabelEl);

        wrap.addEventListener('mouseenter', () => {
          wrap.style.backgroundColor = bgHover;
        });
        wrap.addEventListener('mouseleave', () => {
          wrap.style.backgroundColor = bg;
        });
        wrap.addEventListener('pointerdown', e => {
          e.stopPropagation();
          scrollToSection(segment.target);
        });
        labelContainer.appendChild(wrap);
      });
    } else {
      // Badge-style breadcrumb (non-hierarchy "multi" relationship): exactly
      // 2 independent badges with a gap, e.g. "Fields · Cards". No 3-segment
      // multi case currently exists.
      const parentWrap = document.createElement('div');
      parentWrap.style.cssText = `
        display: flex;
        align-items: center;
        pointer-events: auto;
        cursor: pointer;
        background-color: ${LABEL_BG};
      `;
      parentWrap.dataset.section = section;

      const parentLabelEl = document.createElement('span');
      parentLabelEl.textContent = label;
      parentLabelEl.style.cssText = labelPartStyle;
      parentWrap.appendChild(parentLabelEl);

      parentWrap.addEventListener('mouseenter', () => {
        parentWrap.style.backgroundColor = LABEL_BG_HOVER;
      });
      parentWrap.addEventListener('mouseleave', () => {
        parentWrap.style.backgroundColor = LABEL_BG;
      });
      parentWrap.addEventListener('pointerdown', e => {
        e.stopPropagation();
        scrollToSection(section);
      });
      labelContainer.appendChild(parentWrap);

      const subWrap = document.createElement('div');
      subWrap.style.cssText = `
        display: flex;
        align-items: center;
        pointer-events: auto;
        cursor: pointer;
        background-color: ${LABEL_BG};
        margin-left: 4px;
      `;
      subWrap.dataset.subsection = subsection;

      const subLabelEl = document.createElement('span');
      subLabelEl.textContent = sublabel;
      subLabelEl.style.cssText = labelPartStyle;
      subWrap.appendChild(subLabelEl);

      subWrap.addEventListener('mouseenter', () => {
        subWrap.style.backgroundColor = LABEL_BG_HOVER;
      });
      subWrap.addEventListener('mouseleave', () => {
        subWrap.style.backgroundColor = LABEL_BG;
      });
      subWrap.addEventListener('pointerdown', e => {
        e.stopPropagation();
        scrollToSection(subsection);
      });
      labelContainer.appendChild(subWrap);
    }
  } else {
    // Make the label the hit target (pointer-events: auto) so clicking it
    // navigates to its section WITHOUT falling through to the element beneath
    // (e.g. the price toggle): once the label intercepts, the underlying control
    // is no longer in the event path. Mirrors the parent/sub label wraps above.
    const labelWrap = document.createElement('div');
    labelWrap.style.cssText = `
      display: flex;
      align-items: center;
      pointer-events: auto;
      cursor: pointer;
      background-color: ${LABEL_BG};
    `;
    labelWrap.dataset.section = section;

    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    labelEl.style.cssText = labelPartStyle;
    labelWrap.appendChild(labelEl);

    labelWrap.addEventListener('mouseenter', () => {
      labelWrap.style.backgroundColor = LABEL_BG_HOVER;
    });
    labelWrap.addEventListener('mouseleave', () => {
      labelWrap.style.backgroundColor = LABEL_BG;
    });
    labelWrap.addEventListener('pointerdown', e => {
      e.stopPropagation();
      scrollToSection(section);
    });
    labelContainer.appendChild(labelWrap);
  }

  el.appendChild(labelContainer);

  return el;
}

/**
 * Resolve a block's union companions: extra elements whose rects extend the
 * highlight box (e.g. the PWYW option's label in the option header + its
 * amount input in the option body — no shared wrapper exists, so the map
 * entry declares them via unionWithin/unionClasses).
 */
function resolveUnionElements(result, container) {
  if (!result?.unionClasses?.length) return [];
  const scope =
    (result.unionWithin && result.element.closest(`.${result.unionWithin}`)) || container;
  return result.unionClasses.flatMap(cls => [...scope.querySelectorAll(`.${cls}`)]);
}

function positionOverlay(overlay, target, container, unionEls = []) {
  // The box is the union of the target's rect and any companion rects.
  let { top, left, right, bottom } = target.getBoundingClientRect();
  for (const el of unionEls) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue; // hidden companion
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  const containerRect = container.getBoundingClientRect();
  overlay.style.top = `${top - containerRect.top + container.scrollTop - 1}px`;
  overlay.style.left = `${left - containerRect.left + container.scrollLeft - 1}px`;
  overlay.style.width = `${right - left + 2}px`;
  overlay.style.height = `${bottom - top + 2}px`;
}

/**
 * Hook that enables click-to-scroll from preview elements to design form sections.
 * Only active when the user is on the design tab.
 *
 * Highlights block-level checkout elements with a floating overlay on hover
 * (similar to Figma/Framer element selection) without modifying the element's
 * own styles, so native :hover effects remain visible. Clicking scrolls the
 * design form to the corresponding section.
 *
 * @param {React.RefObject} containerRef - ref to the preview container element
 */
export default function usePreviewClickToSection(containerRef) {
  const location = useLocation();
  const [{ isFullscreen }] = usePreview();
  const isOnDesignTab = location.pathname.includes('/design/theme') && !isFullscreen;
  const highlightedElRef = useRef(null);
  // Companion elements unioned into the current highlight box (see
  // resolveUnionElements) — kept alongside highlightedElRef so scroll/resize
  // repositioning redraws the same union.
  const highlightedUnionRef = useRef([]);
  const overlayRef = useRef(null);
  const lastHoveredSectionRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const resizeObserverRef = useRef(null);

  const clearHighlight = useCallback(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (overlayRef.current) {
      overlayRef.current.remove();
      overlayRef.current = null;
    }
    highlightedElRef.current = null;
    highlightedUnionRef.current = [];
  }, []);

  useEffect(() => {
    if (!isOnDesignTab || !containerRef.current) return;

    const container = containerRef.current;

    // Ensure the container can anchor absolutely-positioned children
    const originalPosition = container.style.position;
    if (
      !getComputedStyle(container).position ||
      getComputedStyle(container).position === 'static'
    ) {
      container.style.position = 'relative';
    }

    // Prevent links inside the preview from navigating.
    const handleClick = e => {
      const link = e.target.closest('a[href]');
      if (link && container.contains(link)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Use pointerdown for scroll-to-section because it fires on disabled
    // elements (click/mousedown don't).
    const handlePointerDown = e => {
      // If clicking a sublabel badge, navigate to that segment's target
      // instead — the deepest one wins (subsection2 > subsection).
      const sublabelTarget = e.target.dataset?.subsection2 || e.target.dataset?.subsection;
      if (sublabelTarget) {
        programmaticScrollRef.current = true;
        scrollToSection(sublabelTarget);
        return;
      }

      const result = findBlockFromTarget(e.target, container);
      if (result) {
        programmaticScrollRef.current = true;
        scrollToSection(result.section);
      }
    };

    const handleMouseOver = e => {
      // Ignore events from within the overlay (labels) to prevent flicker
      if (overlayRef.current && overlayRef.current.contains(e.target)) return;

      const result = findBlockFromTarget(e.target, container);
      if (result) {
        lastHoveredSectionRef.current = result.section;
        if (highlightedElRef.current !== result.element) {
          clearHighlight();
          highlightedElRef.current = result.element;
          const unionEls = resolveUnionElements(result, container);
          highlightedUnionRef.current = unionEls;
          const overlay = createOverlay({
            section: result.section,
            label: result.label,
            subsection: result.subsection,
            sublabel: result.sublabel,
            subsection2: result.subsection2,
            sublabel2: result.sublabel2,
            relationship: result.relationship,
          });
          positionOverlay(overlay, result.element, container, unionEls);
          container.appendChild(overlay);
          overlayRef.current = overlay;

          // Keep the overlay in sync with size changes inside the highlighted
          // block (e.g. a <details> collapsible opening/closing animates the
          // block's height).
          const ro = new ResizeObserver(() => {
            if (overlayRef.current && highlightedElRef.current === result.element) {
              positionOverlay(overlayRef.current, result.element, container, unionEls);
            }
          });
          ro.observe(result.element);
          resizeObserverRef.current = ro;
        }
      } else {
        lastHoveredSectionRef.current = null;
        clearHighlight();
      }
    };

    const handleMouseLeave = () => {
      lastHoveredSectionRef.current = null;
      clearHighlight();
    };

    // When an iframe (e.g. Stripe payment element) is clicked, the parent
    // window loses focus. Scroll to whichever section the mouse was last over.
    const handleWindowBlur = () => {
      if (!lastHoveredSectionRef.current) return;
      const activeEl = document.activeElement;
      if (activeEl?.tagName === 'IFRAME' && container.contains(activeEl)) {
        scrollToSection(lastHoveredSectionRef.current);
      }
    };

    // Hide the overlay while scrolling so it doesn't drift out of sync.
    // We listen for both `scroll` (normal scrolling) and `wheel` (fires
    // during the macOS elastic overscroll bounce where no scroll event is
    // emitted).
    let scrollTimer = null;
    const hideOverlayDuringScroll = () => {
      if (overlayRef.current) {
        overlayRef.current.style.display = 'none';
      }
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        programmaticScrollRef.current = false;
        if (overlayRef.current && highlightedElRef.current) {
          positionOverlay(
            overlayRef.current,
            highlightedElRef.current,
            container,
            highlightedUnionRef.current,
          );
          overlayRef.current.style.display = '';
        }
      }, 100);
    };

    const handleScroll = e => {
      if (programmaticScrollRef.current) return;
      const scrollTarget = e.target;
      if (
        scrollTarget !== document &&
        !container.contains(scrollTarget) &&
        !scrollTarget.contains(container)
      ) {
        return;
      }
      hideOverlayDuringScroll();
    };

    const handleWheel = () => {
      // User is actively scrolling — cancel any programmatic scroll grace period
      programmaticScrollRef.current = false;
      hideOverlayDuringScroll();
    };

    container.addEventListener('pointerdown', handlePointerDown, true);
    container.addEventListener('click', handleClick, true);
    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('wheel', handleWheel, { passive: true });
    // Use capture on window to catch scroll from any ancestor (the scrollable
    // parent is above the container ref in the DOM).
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      clearHighlight();
      clearTimeout(scrollTimer);
      container.style.position = originalPosition;
      container.removeEventListener('pointerdown', handlePointerDown, true);
      container.removeEventListener('click', handleClick, true);
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [isOnDesignTab, containerRef, clearHighlight]);

  return isOnDesignTab;
}
